/** Formatting helpers. All amounts go through Intl.NumberFormat. */

const amountFormatter = new Intl.NumberFormat("en-US", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 6,
});

const rateFormatter = new Intl.NumberFormat("en-US", {
  minimumFractionDigits: 4,
  maximumFractionDigits: 6,
});

const dateFormatter = new Intl.DateTimeFormat("en-US", {
  year: "numeric",
  month: "short",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
});

/** "1234.5" -> "1,234.50". Returns "—" for missing/invalid input. */
export function formatAmount(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === "") return "—";
  const num = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(num)) return "—";
  return amountFormatter.format(num);
}

/** Exchange rate with 4–6 decimals. Returns "—" for invalid input. */
export function formatRate(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === "") return "—";
  const num = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(num)) return "—";
  return rateFormatter.format(num);
}

/** ISO timestamp -> "Aug 15, 2026, 21:04". Returns "—" for invalid input. */
export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return "—";
  return dateFormatter.format(new Date(ms));
}

const intFormatter = new Intl.NumberFormat("en-US", {
  maximumFractionDigits: 0,
});

/** 12345 -> "12,345". Returns "—" for missing/invalid input. */
export function formatInt(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return "—";
  return intFormatter.format(value);
}

const usdCompactFormatter = new Intl.NumberFormat("en-US", {
  notation: "compact",
  maximumFractionDigits: 1,
});

const usd2Formatter = new Intl.NumberFormat("en-US", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/** 1234567 -> "1.2M". Returns "—" for missing/invalid input. */
export function formatUsdCompact(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return "—";
  return usdCompactFormatter.format(value);
}

/** 1234.5 -> "1,234.50" (always 2 decimals). Returns "—" for invalid input. */
export function formatUsd(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return "—";
  return usd2Formatter.format(value);
}

/**
 * "just now", "3 min ago", "2 h ago", "5 d ago": coarse, as `formatDateTime`
 * gives the exact instant. A future time (clock skew) reads "just now".
 */
export function formatRelativeTime(
  iso: string | null | undefined,
  now: number = Date.now(),
): string {
  if (!iso) return "—";
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return "—";
  const seconds = Math.floor((now - ms) / 1000);
  if (seconds < 60) return "just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} h ago`;
  return `${Math.floor(hours / 24)} d ago`;
}

/** "0x1234…abcd" style truncation for hashes/addresses. */
export function shortHash(hash: string, chars = 4): string {
  if (hash.length <= 2 + chars * 2) return hash;
  return `${hash.slice(0, 2 + chars)}…${hash.slice(-chars)}`;
}

/** Seconds until a deadline, floored at 0. A number is unix seconds; a string is ISO. */
export function secondsUntil(deadline: string | number, now: number = Date.now()): number {
  const ms = typeof deadline === 'number' ? deadline * 1000 : Date.parse(deadline);
  if (Number.isNaN(ms)) return 0;
  return Math.max(0, Math.floor((ms - now) / 1000));
}

/** 95 -> "1:35", 7 -> "0:07". */
export function formatCountdown(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds));
  const minutes = Math.floor(s / 60);
  const seconds = s % 60;
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
}
